<template>
  <div class="relative z-0 isolate h-full min-h-0 w-full overflow-hidden rounded-lg border border-gray-100 shadow-lg">
    <div ref="mapContainer" class="h-full min-h-[200px] w-full"></div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  coordinates: {
    type: Object,
    required: true
  },
  amenities: {
    type: Array,
    default: () => []
  },
  selectedId: {
    type: String,
    default: ''
  },
  /** GeoJSON geometry (LineString or similar) from OSRM */
  routeGeometry: {
    type: Object,
    default: null
  },
  /**
   * Straight-line radius (m) around `coordinates` for map framing when no walking route is shown.
   * Use `0` for “show whole area” (fit property + markers). Always pass a number so updates are reactive.
   */
  maxDistanceMeters: {
    type: Number,
    default: 0
  }
});

const emit = defineEmits(['select']);

const { t } = useI18n();

const mapContainer = ref(null);
let map = null;
/** @type {import('leaflet').Marker[]} */
let markers = [];
/** @type {Map<string, import('leaflet').Marker>} */
let markerByAmenityId = new Map();
let routeLayer = null;
let L = null;
let destroyed = false;

/** GeoJSON geometry from OSRM; must not treat `{}` or empty coords as “has route” or distance refits never run. */
function isUsableRouteGeometry(g) {
  if (g == null || typeof g !== 'object') return false;
  const coords = g.coordinates;
  if (!Array.isArray(coords) || coords.length < 2) return false;
  return true;
}

function selectAmenity(id) {
  emit('select', id);
}

function addAmenityMarkers() {
  if (!map || !L) return;

  markers.forEach((marker) => map.removeLayer(marker));
  markers = [];
  markerByAmenityId = new Map();

  props.amenities.forEach((amenity) => {
    const sel = props.selectedId && amenity.id !== props.selectedId;
    const marker = L.marker([amenity.coordinates.lat, amenity.coordinates.lng], {
      opacity: sel ? 0.55 : 1
    })
      .addTo(map)
      .bindPopup(`<div class="p-1"><strong>${amenity.name}</strong></div>`);
    marker.on('click', () => selectAmenity(amenity.id));
    markers.push(marker);
    markerByAmenityId.set(amenity.id, marker);
  });

  if (props.selectedId) {
    const m = markerByAmenityId.get(props.selectedId);
    if (m) {
      m.setOpacity(1);
      m.openPopup();
    }
  }
}

function fitMapToContent() {
  if (!map || !L) return;
  map.invalidateSize();
  const lat = props.coordinates?.lat;
  const lng = props.coordinates?.lng;
  if (typeof lat !== 'number' || typeof lng !== 'number') return;

  const maxM = props.maxDistanceMeters;
  if (Number.isFinite(maxM) && maxM > 0) {
    // Circle.getBounds() needs a map-backed layer (uses projection); a bare L.circle() has no _map.
    const c = L.circle([lat, lng], { radius: maxM, interactive: false }).addTo(map);
    const b = c.getBounds();
    map.removeLayer(c);
    if (b.isValid()) {
      map.fitBounds(b, { padding: [28, 28], maxZoom: 18, animate: false });
    }
    return;
  }

  const points = [[lat, lng]];
  for (const a of props.amenities) {
    if (a?.coordinates && typeof a.coordinates.lat === 'number' && typeof a.coordinates.lng === 'number') {
      points.push([a.coordinates.lat, a.coordinates.lng]);
    }
  }
  if (points.length <= 1) {
    map.setView([lat, lng], 15);
    return;
  }
  map.fitBounds(L.latLngBounds(points), { padding: [44, 44], maxZoom: 16, animate: false });
}

function drawRoute() {
  if (!map || !L) return;
  if (routeLayer) {
    map.removeLayer(routeLayer);
    routeLayer = null;
  }
  const g = props.routeGeometry;
  if (!isUsableRouteGeometry(g)) {
    fitMapToContent();
    return;
  }
  try {
    routeLayer = L.geoJSON(
      { type: 'Feature', geometry: g, properties: {} },
      {
        style: {
          color: '#2563eb',
          weight: 5,
          opacity: 0.85
        }
      }
    ).addTo(map);
    const b = routeLayer.getBounds();
    if (b.isValid()) {
      map.fitBounds(b, { padding: [40, 40], maxZoom: 16 });
    }
  } catch (_) {
    fitMapToContent();
  }
}

/** After layout (flex parent), Leaflet needs a tick before fitBounds/zoom apply reliably. */
function refitWhenNoRoute() {
  if (!map || !L) return;
  if (isUsableRouteGeometry(props.routeGeometry)) return;
  nextTick(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!map || !L || isUsableRouteGeometry(props.routeGeometry)) return;
        map.invalidateSize();
        fitMapToContent();
      });
    });
  });
}

onMounted(async () => {
  if (!mapContainer.value) return;
  const leaflet = await import('leaflet');
  await import('leaflet/dist/leaflet.css');
  if (destroyed || !mapContainer.value) return;
  L = leaflet.default;

  delete L.Icon.Default.prototype._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png'
  });

  map = L.map(mapContainer.value).setView([props.coordinates.lat, props.coordinates.lng], 15);

  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '© OpenStreetMap contributors',
    maxZoom: 19
  }).addTo(map);

  const propertyIcon = L.icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
  });

  L.marker([props.coordinates.lat, props.coordinates.lng], { icon: propertyIcon })
    .addTo(map)
    .bindPopup(`<strong>${t('location.propertyMarkerLabel')}</strong>`);

  addAmenityMarkers();
  drawRoute();
  refitWhenNoRoute();
});

onUnmounted(() => {
  destroyed = true;
  if (map) {
    map.remove();
    map = null;
  }
  markers = [];
  markerByAmenityId = new Map();
  routeLayer = null;
});

watch(
  () => props.amenities,
  () => {
    addAmenityMarkers();
    refitWhenNoRoute();
  },
  { deep: true }
);

watch(
  () => [props.selectedId, props.amenities],
  () => {
    addAmenityMarkers();
  },
  { deep: true }
);

watch(
  () => props.routeGeometry,
  () => {
    drawRoute();
    if (!isUsableRouteGeometry(props.routeGeometry)) {
      refitWhenNoRoute();
    }
  },
  { deep: true }
);

watch(
  () => props.maxDistanceMeters,
  () => {
    refitWhenNoRoute();
  },
  { flush: 'post' }
);

watch(
  () => props.coordinates,
  () => {
    if (map && props.coordinates && typeof props.coordinates.lat === 'number') {
      drawRoute();
      refitWhenNoRoute();
    }
  },
  { deep: true }
);
</script>
